import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";

const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "600px" };
const defaultCenter = { lat: -17.8274, lng: 31.0435 };

const RideShareDriverPassengerIn_TransitRouteTrack = ({
  routePoints = [],
  driverLocation,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries,
  });

  const mapRef = useRef();

  const [currentPosition, setCurrentPosition] = useState(
    driverLocation || defaultCenter
  );
  const [directions, setDirections] = useState(null);
  const [eta, setEta] = useState("");

  // Get driver live position
  useEffect(() => {
    if (!driverLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setCurrentPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      });
    }
  }, [driverLocation]);

  // Build route with up to 10 points
  useEffect(() => {
    if (!isLoaded || !window.google) return;
    if (!routePoints || routePoints.length < 2) return;

    // Limit to max 10 points
    const points = routePoints.slice(0, 10);

    const origin = points[0];
    const destination = points[points.length - 1];

    const waypoints = points.slice(1, points.length - 1).map((p) => ({
      location: p,
      stopover: true,
    }));

    // Validate coordinates
    if (
      !origin.lat ||
      !origin.lng ||
      !destination.lat ||
      !destination.lng
    ) {
      console.error("Invalid route points:", points);
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin,
        destination,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);

          // Calculate total ETA
          const totalSeconds = result.routes[0].legs.reduce(
            (sum, leg) => sum + leg.duration.value,
            0
          );

          setEta(`${Math.ceil(totalSeconds / 60)} min`);
        } else {
          console.error("Directions failed:", status);
        }
      }
    );
  }, [isLoaded, routePoints]);

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div style={{ maxWidth: "900px", margin: "auto" }}>
      <h2>Passenger In-Transit Route Tracking</h2>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={12}
        center={currentPosition}
        onLoad={(map) => (mapRef.current = map)}
      >
        {/* Driver */}
        <Marker
          position={currentPosition}
          title="Driver"
          label="🚗"
        />

        {/* Route points */}
        {routePoints.map((point, index) => (
          <Marker
            key={index}
            position={point}
            label={
              index === 0
                ? "O"
                : index === routePoints.length - 1
                ? "D"
                : `${index}`
            }
          />
        ))}

        {/* Route polyline */}
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: "#800080",
                strokeWeight: 5,
                strokeOpacity: 0.85,
              },
              preserveViewport: false,
              draggable: false,
            }}
          />
        )}
      </GoogleMap>

      <div style={{ marginTop: "10px" }}>
        <p>
          <strong>Total ETA:</strong> {eta || "N/A"}
        </p>
        <p>
          <strong>Stops:</strong>{" "}
          {routePoints.length > 2
            ? routePoints.length - 2
            : 0}
        </p>
      </div>
    </div>
  );
};

export default RideShareDriverPassengerIn_TransitRouteTrack;
